import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { IconButton } from '../IconButton';

const MAX_ENTRIES = 100;
const storageKey = (id) => `TextAreaWithHistory:${id}`;

function loadHistory(id) {
  if (!id) return [];
  try {
    const raw = localStorage.getItem(storageKey(id));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function persistHistory(id, entries) {
  if (!id) return;
  try {
    localStorage.setItem(storageKey(id), JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  } catch {
    // localStorage quota / unavailable — ignore
  }
}

function timeAgo(ts) {
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

const TextAreaWithHistory = forwardRef(function TextAreaWithHistory(
  { id, value, onChange, style, loadLast = true, ...rest },
  ref
) {
  const [history, setHistory] = useState(() => loadHistory(id));
  const [showHistory, setShowHistory] = useState(false);
  const popoverRef = useRef(null);
  const iconBtnRef = useRef(null);

  useEffect(() => {
    if (!loadLast) return;
    if (value) return;
    const last = history[0];
    if (last && onChange) onChange(last.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    save(text) {
      const v = (text ?? '').trim();
      if (!v) return;
      setHistory(prev => {
        const next = [{ value: v, ts: Date.now() }, ...prev.filter(e => e.value !== v)].slice(0, MAX_ENTRIES);
        persistHistory(id, next);
        return next;
      });
    },
    clear() {
      setHistory([]);
      persistHistory(id, []);
    },
    getHistory() {
      return history;
    },
  }), [id, history]);

  useEffect(() => {
    if (!showHistory) return;
    const onDocClick = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (iconBtnRef.current?.contains(e.target)) return;
      setShowHistory(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showHistory]);

  const pickEntry = useCallback((entry) => {
    onChange && onChange(entry.value);
    setShowHistory(false);
  }, [onChange]);

  return (
    <div style={{ position: 'relative', ...style }}>
      <textarea
        {...rest}
        value={value}
        onChange={e => onChange && onChange(e.target.value)}
      />
      <span
        ref={iconBtnRef}
        style={{ position: 'absolute', top: 2, right: 2 }}
      >
        <IconButton
          icon={'history'}
          level={'secondary'}
          title={`Prompt history (${history.length})`}
          onClick={() => setShowHistory(s => !s)}
        />
      </span>

      {showHistory ? (
        <div
          ref={popoverRef}
          className={'border rounded bg-white shadow-sm'}
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            zIndex: 1050,
            width: 'min(640px, 90vw)',
            maxHeight: '50vh',
            overflowY: 'auto',
          }}
        >
          <div className={'d-flex align-items-center px-2 py-1 border-bottom bg-light small'}>
            <strong>Prompt history</strong>
            <span className={'ml-2 text-muted'}>{history.length} / {MAX_ENTRIES}</span>
            <span className={'flex-grow-1'} />
            <button
              className={'btn btn-link btn-sm p-0'}
              disabled={!history.length}
              onClick={() => {
                if (window.confirm('Clear all history entries?')) {
                  setHistory([]);
                  persistHistory(id, []);
                }
              }}
            >
              Clear
            </button>
          </div>

          {history.length === 0 ? (
            <div className={'p-2 text-muted small'}>No history yet.</div>
          ) : (
            <table className={'table table-sm table-hover mb-0 small'}>
              <tbody>
                {history.map((e, i) => (
                  <tr key={i} style={{ cursor: 'pointer' }} onClick={() => pickEntry(e)}>
                    <td
                      className={'text-muted text-nowrap align-top'}
                      style={{ width: '90px' }}
                      title={new Date(e.ts).toLocaleString()}
                    >
                      {timeAgo(e.ts)}
                    </td>
                    <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {e.value.length > 300 ? e.value.slice(0, 300) + '…' : e.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}
    </div>
  );
});

export default TextAreaWithHistory;
